import { useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useSalesSeries } from '@/hooks/useSalesSeries'
import { useChangeEvents } from '@/hooks/useChangeEvents'
import { useProductChannelList } from '@/hooks/useProductChannelList'
import {
  movingAverage, bucketByWeek, periodDelta, densifyDaily, withComparison,
} from '@/lib/products/salesSeriesTransforms'
import type { ChangeEvent } from '@/lib/api/performance'
import { PerformanceFilters } from './PerformanceFilters'
import { PerformanceKpiCards } from './PerformanceKpiCards'
import { PerformanceChart } from './PerformanceChart'
import { PerformanceEventList } from './PerformanceEventList'
import { usePerformanceState } from './usePerformanceState'

interface ProductPerformanceTabProps {
  productId: string
}

export function ProductPerformanceTab({ productId }: ProductPerformanceTabProps) {
  const { t } = useTranslation()
  const [, setSearchParams] = useSearchParams()

  const {
    metric, granularity, rangeDays, compareMode, channelId, types,
    from, to, cmpWin,
    setMetric, setGranularity, setRangeDays, setCompareMode, setChannelId, setTypes,
  } = usePerformanceState()

  const channels = useProductChannelList(productId)

  const salesQ = useSalesSeries(productId, { from, to, channelId: channelId ?? undefined })
  const cmpQ = useSalesSeries(
    productId,
    cmpWin ? { from: cmpWin.from, to: cmpWin.to, channelId: channelId ?? undefined } : {},
    !!cmpWin,
  )
  const eventsQ = useChangeEvents(productId, {
    from, to, channelId: channelId ?? undefined, types,
  })

  const series = densifyDaily(salesQ.data ?? [], from, to)
  const cmp = cmpWin ? densifyDaily(cmpQ.data ?? [], cmpWin.from, cmpWin.to) : []

  const toBase = (s: typeof series) =>
    granularity === 'weekly' ? bucketByWeek(s, metric) : s.map((p) => ({ date: p.date, value: p[metric] }))
  const base = toBase(series)
  const cmpBase = cmpWin ? toBase(cmp) : []
  const ma = granularity === 'weekly' ? [] : movingAverage(series, 7, metric)
  const withCmp = withComparison(base, cmpBase)
  const chartData = withCmp.map((b, i) => ({ ...b, maValue: ma[i]?.value ?? null }))

  const revDelta = periodDelta(series, cmp, 'revenue')
  const qtyDelta = periodDelta(series, cmp, 'quantity')

  const channelName = (id: string | null) =>
    id ? (channels.find((c) => c.id === id)?.name ?? '') : t('performance.channel.all')

  const handleEventClick = useCallback(
    (ev: ChangeEvent) => {
      if (ev.event_type === 'image') {
        setSearchParams({
          tab: 'assets',
          channel: ev.channel_id ?? '',
          slot: String(ev.slot ?? ''),
        })
      } else {
        setSearchParams({
          tab: 'content',
          channel: ev.channel_id ?? '',
          block: ev.block_type ?? '',
        })
      }
    },
    [setSearchParams],
  )

  return (
    <div className="space-y-4">
      <PerformanceFilters
        metric={metric}
        granularity={granularity}
        rangeDays={rangeDays}
        compareMode={compareMode}
        channelId={channelId}
        types={types}
        channels={channels}
        onMetricChange={setMetric}
        onGranularityChange={setGranularity}
        onRangeChange={setRangeDays}
        onCompareChange={setCompareMode}
        onChannelChange={setChannelId}
        onTypesChange={setTypes}
      />
      <PerformanceKpiCards
        revenue={{ sum: revDelta.sum, pct: revDelta.pct }}
        quantity={{ sum: qtyDelta.sum, pct: qtyDelta.pct }}
      />
      <PerformanceChart
        data={chartData}
        events={eventsQ.data ?? []}
        metric={metric}
        onEventClick={handleEventClick}
      />
      <PerformanceEventList
        events={eventsQ.data ?? []}
        channelName={channelName}
        onEventClick={handleEventClick}
      />
    </div>
  )
}
